import * as React from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { PieChart, PieChartDataPoint } from "../PieChart/PieChart";
import { ExportPieChartButton } from "../PieChart/ExportPieChartButton";
import { useQuery } from "react-query";
import { MetricsService } from "../../../gen/clients";

interface Props {
  id: string;
  title: string;
  from: Date;
  to: Date;
  openSearchQuery: Record<string, any>;
  parseResponse: (response: any) => PieChartDataPoint[];
  refetchRef?: React.MutableRefObject<() => void>;
}

const ConnectedPieChart: React.FC<Props> = ({ id, title, from, to, openSearchQuery, parseResponse, refetchRef }) => {
  const fromISO = from.toISOString();
  const toISO = to.toISOString();

  const { data, isLoading, error, isFetching, refetch } = useQuery(
    ["openSearchPieQuery", id, fromISO, toISO, openSearchQuery],
    () =>
      MetricsService.searchIndex({
        indexName: "app-events-v2",
        requestBody: openSearchQuery
      }),
    {
      keepPreviousData: true,
      cacheTime: 0
    }
  );

  React.useEffect(() => {
    if (refetchRef) {
      refetchRef.current = refetch;
    }
  }, [refetch, refetchRef]);

  const chartData = React.useMemo(() => {
    if (!data) {
      return [];
    }
    return parseResponse(data);
  }, [data, parseResponse]);

  const dataIsLoading = isLoading || isFetching;

  const renderContent = () => {
    if (dataIsLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Alert severity="error">Error loading data: {error instanceof Error ? error.message : "Unknown error"}</Alert>
      );
    }
    if (chartData.length === 0) {
      return <Alert severity="info">No data found for the selected time range.</Alert>;
    }
    return undefined;
  };

  const content = renderContent();

  return (
    <Box sx={{ mt: 4 }}>
      <PieChart
        title={title}
        data={!content ? chartData : undefined}
        headerActions={
          !content ? <ExportPieChartButton data={chartData} from={from} to={to} title={title} /> : undefined
        }
      >
        {content}
      </PieChart>
    </Box>
  );
};

export { ConnectedPieChart };
